'use client';

import { useTranslations } from 'next-intl';
import { useMemo, useState } from 'react';
import type { Coin } from '@/lib/api/transaction/models';
import { useCoinsBalance } from '@/lib/providers/CoinsBalanceProvider';
import { TokenIcon } from '@/lib/ui-kit/token-icon';
import { cn } from '@/lib/ui-kit/utils';
import { coinDisplayName } from '@/lib/utils/coinName';
import { formatMaskedMoney, formatMoney } from '@/lib/utils/formatAmount';

interface CoinsListProps {
  coins: Coin[];
  hidden: boolean;
}

const SMALL_BALANCE_USD = 1;
const USD_FORMAT = { prefix: '$', decimals: 2 };

/** The Coins tab (Figma 15073:84519): available balance per coin, largest first. */
export function CoinsList({ coins, hidden }: CoinsListProps) {
  const [isSmallHidden, setIsSmallHidden] = useState(false);
  const { getAvailableBalance } = useCoinsBalance();
  const t = useTranslations('balancePopover');

  const rows = useMemo(
    () =>
      coins
        .map((coin) => {
          const amount = getAvailableBalance(coin.id) ?? 0;
          return { coin, amount, usd: amount * (coin.usdRate ?? 0) };
        })
        .filter((row) => row.amount > 0 && (!isSmallHidden || row.usd >= SMALL_BALANCE_USD))
        .sort((a, b) => b.usd - a.usd),
    [coins, getAvailableBalance, isSmallHidden],
  );

  const smallBalanceLabel = formatMoney(SMALL_BALANCE_USD, { prefix: '$', decimals: 0 });
  const handleToggleSmall = () => setIsSmallHidden((v) => !v);

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-start gap-2 pb-1 text-[12px] text-v2-text-secondary">
        <span className="min-w-0 flex-1">{t('availableBalance')}</span>
        <button
          type="button"
          onClick={handleToggleSmall}
          aria-pressed={isSmallHidden}
          className={cn(
            'shrink-0 cursor-pointer leading-[1.15] transition-colors hover:text-v2-text-primary',
            isSmallHidden && 'text-v2-text-primary',
          )}
        >
          {t('hideSmall', { amount: smallBalanceLabel })}
        </button>
      </div>
      {rows.length === 0 ? (
        <p className="py-3 text-[13px] text-v2-text-secondary">{t('noCoins')}</p>
      ) : (
        <div className="scrollbar-thumb-thin flex max-h-[142px] flex-col gap-0.5 overflow-y-auto overscroll-contain pr-2">
          {rows.map(({ coin, amount, usd }) => (
            <div key={coin.id} className="flex items-center gap-2 py-1.5">
              <TokenIcon symbol={coin.shortName} iconUrl={coin.iconUrl} size={24} />
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <span className="text-[13px] leading-4 tracking-[-0.13px] text-v2-text-primary">
                  {coin.shortName}
                </span>
                <span className="truncate text-[12px] leading-[1.15] text-v2-text-secondary">
                  {coinDisplayName(coin)}
                </span>
              </div>
              <div className="ph-no-capture flex shrink-0 flex-col items-end gap-1 tabular-nums">
                <span className="text-[13px] leading-4 tracking-[-0.13px] text-v2-text-primary">
                  {formatMaskedMoney(amount, hidden, { decimals: coin.decimalPlacesShort })}
                </span>
                <span className="text-[12px] leading-[1.15] text-v2-text-secondary">
                  {formatMaskedMoney(usd, hidden, USD_FORMAT)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
